import classNames from 'classnames';
import React, { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import Label from '../shared/Label';

import s from './TextArea.module.scss';

export type TextAreaOnBlurProps = {
  description?: string;
  disabled?: boolean;
  error?: string;
  hasError?: boolean;
  hint?: string;
  id?: string;
  label: string;
  name?: string;
  onChange: (value: string, event: ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  optional?: boolean;
  placeholder?: string;
  rows?: number;
  style?: React.CSSProperties;
  value?: string | null;
};

const TextAreaOnBlur: React.FC<TextAreaOnBlurProps> = ({
  description,
  disabled,
  error,
  hasError,
  hint,
  id,
  label,
  name,
  onChange,
  onFocus,
  onBlur,
  optional,
  placeholder,
  rows,
  style,
  value,
}) => {
  const [textValue, setTextValue] = useState(value ?? '');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    setTextValue(value ?? '');
  }, [value]);

  const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>): void => {
    setTextValue(event.target.value);
  }, []);

  const handleOnBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>): void => {
      onChangeRef.current(textValue, event);
      onBlur?.();
    },
    [textValue, onBlur],
  );

  return (
    <Label label={label} required={!optional} description={description} errorMessage={error} hint={hint}>
      <textarea
        id={id}
        name={name}
        value={textValue}
        placeholder={placeholder}
        required={!optional}
        onChange={handleChange}
        className={classNames(s['input-field'], s.textarea, (hasError || !!error) && s.error)}
        disabled={disabled}
        rows={rows}
        style={style}
        onFocus={onFocus}
        onBlur={handleOnBlur}
      />
    </Label>
  );
};

export default TextAreaOnBlur;
